"use client"

import DHero from "@/features/client/sections/Travel/DHero"
import { PackagesSection } from "@/features/client/sections/Travel/Package"
import { PackageCardSkeleton } from "@/features/client/components/packages/ClientPackageComponentsExports"
import { Skeleton } from "@/shared/components/ui/skeleton"
import { RegionFilter, type TravelType } from "@/features/client/sections/Travel/Filter"
import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { RefreshCw } from "lucide-react"
import type { Region } from "@/shared/types/packages"
import { api, type PackageData } from "@/shared/lib/api/ApiClient"
import type { TravelPackage } from "@/shared/types/packages"

// Adapter to convert API package to TravelPackage
function adaptToTravelPackage(apiPackage: PackageData): TravelPackage {

  
  return {
    id: apiPackage.id,
    title: apiPackage.title,
    location: apiPackage.location,
    duration: apiPackage.days * apiPackage.nights, // Keep for compatibility
    days: apiPackage.days,
    nights: apiPackage.nights,
    category: apiPackage.category,
    currency: apiPackage.currency,
    price: apiPackage.price,
    image: apiPackage.image || "",
    visaRequired: apiPackage.visa_required,
    visaInfo: apiPackage.visa_info,
    overview: apiPackage.overview,
    highlights: apiPackage.packageHighlights,
    packageHighlights: apiPackage.packageHighlights,
    itinerary: (apiPackage.itinerary || []).map(day => ({
      ...day,
      accommodation: day.accommodation || "",
    })),
    inclusions: apiPackage.inclusions,
    exclusions: apiPackage.exclusions
  }
}

export default function Destination() {
  const [searchParams] = useSearchParams()
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null)
  const [allPackages, setAllPackages] = useState<TravelPackage[]>([])
  const [filteredPackages, setFilteredPackages] = useState<TravelPackage[]>([])
  const [travelType, setTravelType] = useState<TravelType>("all")
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [refreshing, setRefreshing] = useState(false)


  // Enhanced search function that searches through all package fields
  const searchPackages = (packages: TravelPackage[], query: string) => {
    if (!query.trim()) return packages

    const searchTerm = query.toLowerCase().trim()
    return packages.filter(pkg => {
      return (
        // Basic package info
        pkg.title.toLowerCase().includes(searchTerm) ||
        pkg.location.toLowerCase().includes(searchTerm) ||
        pkg.category.toLowerCase().includes(searchTerm) ||
        pkg.overview?.toLowerCase().includes(searchTerm) ||
        
        // Duration and pricing
        pkg.price.toString().includes(searchTerm) ||
        pkg.days.toString().includes(searchTerm) ||
        pkg.nights.toString().includes(searchTerm) ||
        
        // Package highlights
        pkg.packageHighlights?.some(highlight => 
          highlight.toLowerCase().includes(searchTerm)
        ) ||
        pkg.highlights?.some(highlight => 
          highlight.toLowerCase().includes(searchTerm)
        ) ||
        
        // Inclusions
        pkg.inclusions?.some(inclusion => 
          inclusion.toLowerCase().includes(searchTerm)
        ) ||
        
        // Exclusions
        pkg.exclusions?.some(exclusion => 
          exclusion.toLowerCase().includes(searchTerm)
        ) ||
        
        // Itinerary
        pkg.itinerary?.some(day => 
          day.title.toLowerCase().includes(searchTerm) ||
          day.activities?.some(activity => {
            if (typeof activity === 'string') {
              return activity.toLowerCase().includes(searchTerm);
            } else if (activity && typeof activity === 'object' && 'activity' in activity) {
              return activity.activity.toLowerCase().includes(searchTerm) ||
                activity.subActivities?.some(sub => sub.toLowerCase().includes(searchTerm)) || false;
            }
            return false;
          }) ||
          day.meals?.some(meal => 
            meal.toLowerCase().includes(searchTerm)
          ) ||
          day.accommodation?.toLowerCase().includes(searchTerm)
        ) ||
        
        // Visa information
        pkg.visaInfo?.toLowerCase().includes(searchTerm)
      )
    })
  }

  // Reusable fetch function
  const fetchPackages = async (showLoader = false) => {
    try {
      if (showLoader) setLoading(true)
      else setRefreshing(true)
      
      // Add cache-busting timestamp to force fresh data
      const response = await api.packages.getAll({ 
        status: "active",
        _t: Date.now() // Cache buster
      })
      
      if (response.success && response.data) {
        const packages = response.data.map(adaptToTravelPackage)
        setAllPackages(packages)
        
        // Check for search parameter and apply search if present
        const urlSearchQuery = searchParams.get('search')
        if (urlSearchQuery) {
          setSearchQuery(urlSearchQuery)
          const searchResults = searchPackages(packages, urlSearchQuery)
          setFilteredPackages(searchResults)
        } else {
          setFilteredPackages(packages)
        }
      }
    } catch (error) {
      console.error("Failed to fetch packages:", error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  // Manual refresh function
  const handleRefresh = () => {
    fetchPackages(false)
  }

  // Fetch packages from API on mount
  useEffect(() => {
    fetchPackages(true)
  }, [searchParams])

  // Auto-refresh every 5 minutes to catch admin updates
  useEffect(() => {
    const interval = setInterval(() => {
      // Only refresh if not currently loading
      if (!loading && !refreshing) {
        fetchPackages(false)
      }
    }, 300000) // 5 minutes

    return () => clearInterval(interval)
  }, [loading, refreshing])

  // Apply search when searchQuery changes
  useEffect(() => {
    if (searchQuery) {
      const searchResults = searchPackages(allPackages, searchQuery)
      setFilteredPackages(searchResults)
    } else {
      setFilteredPackages(allPackages)
    }
  }, [searchQuery, allPackages])

  if (loading) {
    return (
      <main className="min-h-screen bg-white">
        <section className="max-w-7xl mx-auto container px-4 py-12 sm:py-16 mb-8">
          <div className="mb-10 sm:mb-12">
            <Skeleton className="h-8 w-64 mb-3" />
            <Skeleton className="h-5 w-3/4" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="animate-in fade-in">
                <PackageCardSkeleton />
              </div>
            ))}
          </div>
        </section>
      </main>
    )
  }

  return (
    <main className="min-h-screen bg-white">

      <DHero 
        travelType={travelType} 
        searchQuery={searchQuery} 
        filteredCount={filteredPackages.length} 
      />

      <RegionFilter
        selectedRegion={selectedRegion}
        onRegionChange={setSelectedRegion}
        onFilteredPackagesChange={setFilteredPackages}
        onTravelTypeChange={setTravelType}
        allPackages={allPackages}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
      />

      {/* Refresh indicator and button */}
      {refreshing && (
        <div className="max-w-7xl mx-auto px-4 mb-4">
          <div className="flex items-center justify-center py-2 bg-orange-50 rounded-lg border border-orange-200">
            <RefreshCw className="w-4 h-4 mr-2 text-orange-600 animate-spin" />
            <span className="text-sm text-orange-700">Refreshing packages...</span>
          </div>
        </div>
      )}

      <PackagesSection 
        filteredPackages={filteredPackages} 
        selectedRegion={selectedRegion}
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />
    </main>
  )
}